import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { isDesktop } from '../lib/api';
import { getDesktopToken } from '../lib/desktopToken';
import { cacheCareSnapshot, getCachedCareSnapshot, queueCareAction, getPendingCareActions, removePendingCareAction } from './careOfflineCache';

const Context = createContext(null);
export const useCare = () => useContext(Context);
export const errorText = e => typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : e?.message || 'Request failed';
const isNetworkError = e => !e?.response;

// Extracted out of the provider so the useMemo body in CareProvider stays a
// single readable call instead of inline axios setup + interceptor logic.
function buildCareApi(patientMode, pharmacy, branch) {
  const instance = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/care`, withCredentials: true });
  instance.interceptors.request.use(config => {
    // Patient portal and staff web both authenticate via their httpOnly
    // cookie (`portal_session` / `session_token`) sent automatically by
    // withCredentials — only the desktop staff terminal still needs a
    // manually-attached Bearer token (see lib/api.js::isDesktop).
    if (!patientMode && isDesktop()) {
      const token = getDesktopToken();
      if (token) config.headers.Authorization = 'Bearer ' + token;
    }
    if (patientMode) config.headers['X-Care-Portal'] = 'patient';
    if (pharmacy) config.headers['X-Portal-Pharmacy'] = pharmacy;
    if (branch && patientMode) config.headers['X-Portal-Branch'] = branch;
    return config;
  });
  return instance;
}

export const CareProvider = ({ patientMode = false, pharmacy, branch, user, children }) => {
  const api = useMemo(
    () => buildCareApi(patientMode, pharmacy, branch),
    [patientMode, pharmacy, branch],
  );

  const uploadFile = async (file, purpose, tenantId, centralReview = false) => {
    const form = new FormData();
    form.append('file', file); form.append('purpose', purpose);
    if (tenantId) form.append('tenant_id', tenantId);
    if (centralReview) form.append('central_review', 'true');
    const { data } = await api.post('/files', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.file_id;
  };

  const [pendingActions, setPendingActions] = useState([]);
  const refreshPendingActions = useCallback(async () => {
    try { setPendingActions(await getPendingCareActions()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { refreshPendingActions(); }, [refreshPendingActions]);

  // A clinical decision is finalised entirely client-side before this is
  // ever called — a network error here just means the actual POST to the
  // server failed, not that the decision itself is invalid. We queue it
  // instead of losing the pharmacist's work, and surface it for an explicit
  // manual "Submit now" once reconnected (never auto-replayed silently,
  // since this is a clinical action, not a POS sale).
  const queueOrSend = async (method, url, body) => {
    try {
      const res = await api.request({ method, url, data: body });
      return { queued: false, data: res.data };
    } catch (e) {
      if (isNetworkError(e)) {
        await queueCareAction({ method, url, body });
        await refreshPendingActions();
        return { queued: true };
      }
      throw e;
    }
  };

  const retryPendingAction = async (localId) => {
    const action = pendingActions.find(a => a.local_id === localId);
    if (!action) return;
    try {
      await api.request({ method: action.method, url: action.url, data: action.body });
      await removePendingCareAction(localId);
      await refreshPendingActions();
      toast.success('Pending clinical action submitted');
    } catch (e) {
      if (!isNetworkError(e)) {
        // A real rejection (e.g. the case moved on while this was queued) —
        // surface it instead of retrying forever.
        toast.error(errorText(e));
      }
      throw e;
    }
  };

  return <Context.Provider value={{ api, patientMode, branch, pharmacy, user, uploadFile, queueOrSend, pendingActions, retryPendingAction, refreshPendingActions }}>{children}</Context.Provider>;
};

export const useLiveCare = (path, initial = {}) => {
  const { api } = useCare();
  const [data, setData] = useState(initial), [error, setError] = useState(''), [loading, setLoading] = useState(true);
  const cacheKey = 'req:' + path;
  const refresh = useCallback(async signal => {
    try {
      const response = await api.get(path, { signal });
      setData(response.data);
      setError('');
      cacheCareSnapshot(cacheKey, response.data).catch(() => {});
    } catch (e) {
      if (e.code === 'ERR_CANCELED') return;
      if (isNetworkError(e)) {
        // Offline — fall back to the last successful snapshot instead of
        // showing an empty/error state.
        const cached = await getCachedCareSnapshot(cacheKey);
        if (cached) { setData(cached); setError(''); } else { setError(errorText(e)); }
      } else {
        setError(errorText(e));
      }
    } finally { setLoading(false); }
  }, [api, path, cacheKey]);
  useEffect(() => {
    const controller = new AbortController(); let busy = false;
    const update = async () => { if (document.hidden || busy) return; busy = true; await refresh(controller.signal); busy = false; };
    update(); const timer = setInterval(update, 5000); window.addEventListener('online', update); document.addEventListener('visibilitychange', update);
    return () => { controller.abort(); clearInterval(timer); window.removeEventListener('online', update); document.removeEventListener('visibilitychange', update); };
  }, [refresh]);
  return { data, error, loading, refresh: () => refresh() };
};
